import { useState, useRef, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, Maximize2 } from "lucide-react";

export const VideoSection = () => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-20 bg-background relative overflow-hidden">
      {/* Animated background orbs */}
      <div className="absolute top-1/4 -left-20 w-72 h-72 bg-primary/10 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-1/4 -right-20 w-72 h-72 bg-accent/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

      <div className="container mx-auto px-6 relative z-10">
        <div 
          className={`max-w-5xl mx-auto transition-all duration-1000 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"
          }`}
        >
          {/* Section header */}
          <div className="text-center mb-10">
            <span className="inline-block text-sm font-medium text-primary mb-3 tracking-wider uppercase">
              Découvrir
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground font-display">
              Voyez Floop en <span className="bg-gradient-floop bg-clip-text text-transparent">action</span>
            </h2>
          </div>

          {/* Video Container with 3D effect */}
          <div 
            className="relative group perspective-1000"
            style={{ 
              transform: isVisible ? "rotateX(0deg)" : "rotateX(10deg)",
              transition: "transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)"
            }}
          >
            {/* Glow effect */}
            <div className="absolute -inset-1 bg-gradient-floop rounded-3xl blur-xl opacity-20 group-hover:opacity-40 transition-opacity duration-500" />
            
            {/* Main video container */}
            <div className="relative aspect-video bg-card/80 backdrop-blur-xl rounded-3xl border border-border/50 overflow-hidden shadow-2xl">
              {/* Gradient mesh background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
              
              {/* Grid pattern overlay */}
              <div 
                className="absolute inset-0 opacity-5"
                style={{
                  backgroundImage: "linear-gradient(hsl(var(--primary) / 0.3) 1px, transparent 1px), linear-gradient(90deg, hsl(var(--primary) / 0.3) 1px, transparent 1px)",
                  backgroundSize: "50px 50px"
                }}
              />
              
              {/* Center play button */}
              <div className="absolute inset-0 flex items-center justify-center">
                <button 
                  onClick={() => setIsPlaying(!isPlaying)}
                  className="relative group/btn"
                  aria-label={isPlaying ? "Pause video" : "Play video"}
                >
                  {/* Ripple effect */}
                  <div className="absolute inset-0 rounded-full bg-primary/30 animate-ping" />
                  <div className="absolute -inset-4 rounded-full bg-primary/10 animate-pulse" />
                  
                  {/* Button */}
                  <div className="relative w-24 h-24 rounded-full bg-gradient-floop flex items-center justify-center shadow-2xl shadow-primary/40 group-hover/btn:scale-110 transition-transform duration-300">
                    {isPlaying ? (
                      <Pause className="w-10 h-10 text-white" fill="currentColor" />
                    ) : (
                      <Play className="w-10 h-10 text-white ml-1.5" fill="currentColor" />
                    )}
                  </div>
                </button>
              </div>

              {/* Bottom controls bar */}
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-background/90 to-transparent">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    {/* Progress bar */}
                    <div className="w-48 h-1.5 bg-muted rounded-full overflow-hidden">
                      <div className="w-1/3 h-full bg-gradient-floop rounded-full" />
                    </div>
                    <span className="text-sm text-muted-foreground font-mono">0:00 / 2:30</span>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <button 
                      onClick={() => setIsMuted(!isMuted)}
                      className="p-2 rounded-full hover:bg-muted/50 transition-colors"
                      aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                      {isMuted ? (
                        <VolumeX className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <Volume2 className="w-5 h-5 text-foreground" />
                      )}
                    </button>
                    <button 
                      className="p-2 rounded-full hover:bg-muted/50 transition-colors"
                      aria-label="Fullscreen"
                    >
                      <Maximize2 className="w-5 h-5 text-muted-foreground" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Floating badges */}
              <div className="absolute top-6 left-6 flex items-center gap-2">
                <span className="px-3 py-1.5 bg-background/80 backdrop-blur-sm rounded-full text-xs font-medium text-foreground border border-border/50">
                  HD 1080p
                </span>
                <span className="px-3 py-1.5 bg-primary/20 backdrop-blur-sm rounded-full text-xs font-medium text-primary border border-primary/30">
                  Démo officielle
                </span>
              </div>
            </div>
          </div>

          {/* Caption */}
          <p className="text-center text-sm text-muted-foreground mt-6">
            Découvrez comment créer des visuels professionnels en moins de 2 minutes
          </p>
        </div>
      </div>
    </section>
  );
};
